import React from 'react';
import { useNavigate } from 'react-router-dom';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useLayoutContext } from '../../layouts/general-layout';
import { useUserStore } from '../../utils/userStore';
import { useGamificationStore } from '../../utils/gamificationStore';
import { logout } from '../../utils/authUtils';
import { RouterConstant } from '../../routers/index';
import {
  shoonitnewlogo,
  headerTrophyIcon as trophyIcon,
  userIcon,
  homePageIcon,
  infoIcon,
  studySubjectsIcon,
  targetsIcon,
  settingsIcon,
  logoutIcon,
  closeMenuIcon,
  studyIcon,
  exerciseIcon,
  homeworkIcon,
  examIcon,
  openMissionsIcon
} from '../../assets/images/index';

import './side-menu.scss';
import { UserAvatar } from './UserAvatar';

interface SideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SideMenu: React.FC<SideMenuProps> = ({ isOpen, onClose }) => {
  const { t } = useLayoutContext();
  const navigate = useNavigate();
  const { ROUTERS_PATH } = RouterConstant;

  const { userInfo, user_attributes, getGradeLabel, getSchoolLabel, isAdmin, isTeacher } = useUserStore();
  const { totalPoints, levelKey, levelLabel, levelIcon } = useGamificationStore();

  const fullName = `${userInfo?.first_name || ''} ${userInfo?.last_name || ''}`.trim();

  // Extract school info using resolved labels
  const schoolName = getSchoolLabel();
  const grade = getGradeLabel();

  const schoolDetails = [schoolName, grade].filter(Boolean).join(', ');

  const modes = [
    { id: 'study', label: t('v2ModeStudy'), icon: studyIcon, path: ROUTERS_PATH.SEARCH_LESSON },
    { id: 'exercise', label: t('v2ModeExercise'), icon: exerciseIcon, path: ROUTERS_PATH.SEARCHEXRCISE },
    { id: 'homework', label: t('v2ModeHomework'), icon: homeworkIcon, path: ROUTERS_PATH.EXERCISES_HOMEWORK },
    { id: 'exam', label: t('v2ModeExam'), icon: examIcon, path: ROUTERS_PATH.SELFTESTSTART },
  ] as const;

  const navItems = [
    { label: t('homePage'), icon: homePageIcon, path: ROUTERS_PATH.USER_MAIN_DASHBOARD },
    { label: t('notificationCenter'), icon: MailOutlineIcon, path: ROUTERS_PATH.NOTIFICATIONS },
    { label: t('openMissions'), icon: openMissionsIcon, path: ROUTERS_PATH.USER_ALL_MISSIONS },
    { label: t('settings'), icon: settingsIcon, path: ROUTERS_PATH.USERPROFILE },
    { label: t('gettingToKnowShoonit'), icon: infoIcon, path: ROUTERS_PATH.V2_ONBOARDING },
  ];

  const handleNavClick = (path: string) => {
    if (window.location.pathname === path) {
      navigate(path, { state: { reset: true, timestamp: Date.now() } });
    } else {
      navigate(path);
    }
    onClose();
  };

  const handleLogout = () => {
    logout(navigate);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`side-menu-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className="side-menu-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="side-menu-header-wrapper">
          <div className="side-menu-header">
            <div className="score-badge">
              <img src={trophyIcon} alt="trophy" className="score-icon" />
              <span className="score-value">{levelLabel || t(`gamification.level.${levelKey}`)}</span>
            </div>

            <div className="header-actions">
              <button className="close-btn" onClick={onClose}>
                <img src={closeMenuIcon} alt="close" />
              </button>
            </div>
          </div>
        </div>

        {/* User Profile Section */}
        <div className="user-profile-section">
          <UserAvatar
            levelKey={levelKey}
            levelIcon={levelIcon}
            userImage={userInfo?.avatar}
          />
          <div className="user-info">
            <h2 className="user-name">{fullName || t('guest')}</h2>
            <p className="user-details">{schoolDetails}</p>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="side-menu-nav">
          <div className="nav-section-title">{t('studyMethods')}</div>

          {/* Study Modes Section */}
          <div className="side-menu-modes">
            {modes.map((mode) => (
              <div
                key={mode.id}
                className="side-menu-mode-card"
                onClick={() => handleNavClick(mode.path)}
              >
                <div className="mode-icon-wrapper">
                  <img src={mode.icon} alt={mode.id} />
                </div>
                <span className="mode-label">{mode.label}</span>
              </div>
            ))}
          </div>

          {navItems.map((item, index) => {
            const IconComp = typeof item.icon !== 'string' ? item.icon : null;
            return (
              <div
                key={index}
                className="nav-item"
                onClick={() => handleNavClick(item.path)}
              >
                {IconComp ? (
                  <IconComp className="nav-icon nav-icon--mui" />
                ) : (
                  <img src={item.icon as string} alt={item.label} className="nav-icon" />
                )}
                <span className="nav-label">{item.label}</span>
              </div>
            );
          })}

          <div className="nav-item logout-item" onClick={handleLogout}>
            <img src={logoutIcon} alt="logout" className="nav-icon" />
            <span className="nav-label">{t('exit')}</span>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default SideMenu;
